import React, { useCallback } from 'react';

import Button from '@atlaskit/button';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import { useCreateContentFromTemplate } from '@confluence/create-content-from-template/entry-points/useCreateContentFromTemplate.ts';
import { refetchEditorContent } from '@confluence/editor-features/entry-points/refetchEditorContent.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { RevampedTemplateIcon } from '@confluence/experiment-land-users-in-editor-with-revamped-bpqa/entry-points/RevampedTemplateIcon.tsx';
import { useBlankPageQuickActionsContext } from './BlankPageQuickActionsContext.tsx';
import type { useSuggestedTemplatesQuery$data } from './__generated__/useSuggestedTemplatesQuery.graphql.ts';
import { QuickCardContainer, ButtonText, QuickCardColumn, QuickCardIcon } from './bpqaStyled.tsx';
import { QuickTemplateIcon } from './QuickTemplateIcon.tsx';

type Template = NonNullable<
	NonNullable<NonNullable<useSuggestedTemplatesQuery$data['allTemplates']>['nodes']>[number]
>;

export type QuickTemplateType = Template;

export type QuickTemplateCardProps = {
	template: QuickTemplateType;
	disabled: boolean;
	spaceKey: string;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	index: number;
	contentId: string;
	title: string | null;
	cancelDebouncedSaveDraft: () => void;
	showSpinner: () => void;
	hideSpinner: () => void;
	onSuccess: ({ templateName }: { templateName: string }) => void;
	setTemplateIndex: (value: number) => void;
	handleMouseEnter: () => void;
	handleMouseLeave: () => void;
};

export const QuickTemplateCard = ({
	template,
	disabled,
	spaceKey,
	createAnalyticsEvent,
	index,
	contentId,
	title,
	cancelDebouncedSaveDraft,
	showSpinner,
	hideSpinner,
	onSuccess,
	setTemplateIndex,
	handleMouseEnter,
	handleMouseLeave,
}: QuickTemplateCardProps) => {
	const routeDataRef = useRouteDataRef();
	const isLivePage = useIsCurrentPageLive();
	const { revampedBpqa } = useBlankPageQuickActionsContext();

	const createFromTemplate = useCreateContentFromTemplate();
	const [hovered, setHovered] = React.useState<boolean>(false);
	const getTemplateName = (templateName: string) => {
		let name = templateName;
		if (templateName.length > 20) {
			name = `${templateName.substring(0, 20)}...`;
		}
		return name;
	};

	const blueprintModuleCompleteKey = template.blueprintModuleCompleteKey || '';

	const handleError = () => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'QuickTemplateCard',
				action: 'error',
				actionSubject: 'createFromTemplate',
				attributes: {
					version: '2',
					revampedBpqa,
				},
			},
		}).fire();
	};

	const recoverAndRefreshEditorContent = useCallback(async () => {
		await refetchEditorContent(contentId);
		hideSpinner();
	}, [contentId, hideSpinner]);

	const beforeUpdateRequest = () => {
		showSpinner();
		cancelDebouncedSaveDraft();
	};

	const templateId = template.contentBlueprintId || template.templateId;
	const templateName = template.name;

	const handleTemplateSelect = async () => {
		try {
			cancelDebouncedSaveDraft();
			const options = {
				onContentUpdated: recoverAndRefreshEditorContent,
				onVariableInputError: handleError,
				templateOriginSpaceKey: spaceKey,
				beforeUpdateRequest,
				contentId,
				...(title && { title }),
			};
			await createFromTemplate(
				{ ...template, categoryIds: template.categoryIds ? [...template.categoryIds] : undefined },
				spaceKey,
				options,
			);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'QuickTemplateCard',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'templateOption',
					attributes: {
						id: blueprintModuleCompleteKey || templateId,
						currentRouteName: routeDataRef?.current?.routeName,
						currentRouteIsLive: isLivePage,
						version: '2',
						revampedBpqa,
					},
				},
			}).fire();
			onSuccess({
				templateName: templateName || '',
			});
		} catch {
			handleError();
		}
	};

	return (
		<QuickCardContainer data-testid={`quick-template-card-${templateId}`} isDisabled={disabled}>
			<Button
				spacing="compact"
				testId={`quick-template-card-button-${templateId}`}
				isDisabled={disabled}
				shouldFitContainer
				onClick={handleTemplateSelect}
				onMouseEnter={() => {
					handleMouseEnter();
					setTemplateIndex(index);
					if (revampedBpqa) setHovered(true);
				}}
				onMouseLeave={() => {
					handleMouseLeave();
					if (revampedBpqa) setHovered(false);
				}}
				iconBefore={
					<QuickCardIcon isDisabled={disabled}>
						{revampedBpqa ? (
							<RevampedTemplateIcon
								blueprintModuleCompleteKey={blueprintModuleCompleteKey}
								hovered={hovered}
							/>
						) : (
							<QuickTemplateIcon blueprintModuleCompleteKey={blueprintModuleCompleteKey} />
						)}
					</QuickCardIcon>
				}
			>
				<QuickCardColumn>
					<ButtonText data-testid={`quick-template-card-name-${templateId}`} isDisabled={disabled}>
						{getTemplateName(template.name ?? '')}
					</ButtonText>
				</QuickCardColumn>
			</Button>
		</QuickCardContainer>
	);
};
